import React from 'react'
import { useAgTechQuery, useAgTechQueryResultCombination } from '@agtech/core/data/queries/AgTechDataQueries'
import { OperationDataGetOperationStatesQuery } from '@agtech/data/operations/OperationDataQueries'
import { AgTechUserProfileRoute } from '@agtech/data/users/AgTechUserProfileData'
import { UserDataGetMyProfileQuery } from '@agtech/data/users/AgTechUserProfileQueries'
import { AgTechButton, AgTechButtonProps, AgTechClickEventHandler } from '../../../components/Buttons/AgTechButtons'
import { AgTechFormReadOnlyTextControl } from '../../../components/Forms/Controls/AgTechFormReadOnlyTextControl'
import { AgTechFormRow } from '../../../components/Forms/Layout/AgTechFormRow'
import AgTechHeaderDetail from '../../../components/Headers/AgTechHeaderDetail'
import AgTechViewDialog from '../../../components/Portals/Dialogs/AgTechViewDialog'
import MyAddressEditDialog from './MyAddressEditDialog'
import MyProfileEditDialog from './MyProfileEditDialog'

export type MyProfileDialogProps = {
    route: AgTechUserProfileRoute
}

// Lets a user view and manage their own name, phone and address. Email is shown read-only and passwords
// are deliberately not changeable from here.
const MyProfileDialog = (props: MyProfileDialogProps) => {
    const profileQuery = useAgTechQuery({
        query: UserDataGetMyProfileQuery,
        queryContext: { route: props.route }
    });

    const statesQuery = useAgTechQuery({
        query: OperationDataGetOperationStatesQuery
    });

    const dialogQuery = useAgTechQueryResultCombination({
        firstQuery: profileQuery,
        secondQuery: statesQuery,
        projection: (profile, states) => ({ profile, states })
    });

    return (
        <AgTechViewDialog
            data={{
                query: dialogQuery
            }}
            structure={{
                header: () => ({
                    header: 'My Profile',
                    detail: 'Review and update your own account details'
                }),
                width: '650px'
            }}
        >
            {({ profile, states }) => {
                const address = profile.address;
                const state = states.find(s => s.id === address?.stateProvinceId);

                return (
                    <div className='row-based p-body gap-y-6 flex'>
                        <div id='my-profile-info'>
                            <AgTechHeaderDetail
                                header='My Information'
                                settings={{
                                    header: { classes: 'text-more font-semibold' },
                                    content: {
                                        body: () => (
                                            <MyProfileEditDialog.Button route={props.route} classes='ml-auto button-link pr-2' />
                                        )
                                    },
                                    classes: 'mb-4'
                                }}
                            />
                            <AgTechFormRow>
                                <AgTechFormReadOnlyTextControl
                                    entityType={profile}
                                    caption='First Name'
                                    value={() => profile.firstName}
                                />
                                <AgTechFormReadOnlyTextControl
                                    entityType={profile}
                                    caption='Last Name'
                                    value={() => profile.lastName}
                                />
                            </AgTechFormRow>
                            <AgTechFormRow>
                                <AgTechFormReadOnlyTextControl
                                    entityType={profile}
                                    caption='Email Address'
                                    value={() => profile.email}
                                />
                                <AgTechFormReadOnlyTextControl
                                    entityType={profile}
                                    caption='Phone Number'
                                    value={() => profile.phoneNumber ?? ''}
                                />
                            </AgTechFormRow>
                        </div>
                        <div id='my-profile-address' className='border-t pt-6'>
                            <AgTechHeaderDetail
                                header='My Address'
                                settings={{
                                    header: { classes: 'text-more font-semibold' },
                                    content: {
                                        body: () => (
                                            <MyAddressEditDialog.Button route={props.route} classes='ml-auto button-link pr-2' />
                                        )
                                    },
                                    classes: 'mb-4'
                                }}
                            />
                            <AgTechFormRow>
                                <AgTechFormReadOnlyTextControl
                                    entityType={profile}
                                    caption='Address Line One'
                                    value={() => address?.lineOne ?? ''}
                                />
                                <AgTechFormReadOnlyTextControl
                                    entityType={profile}
                                    caption='Address Line Two'
                                    value={() => address?.lineTwo ?? ''}
                                />
                            </AgTechFormRow>
                            <AgTechFormRow>
                                <AgTechFormReadOnlyTextControl
                                    entityType={profile}
                                    caption='City'
                                    value={() => address?.city ?? ''}
                                />
                                <AgTechFormReadOnlyTextControl
                                    entityType={profile}
                                    caption='State'
                                    value={() => state?.name ?? ''}
                                />
                                <AgTechFormReadOnlyTextControl
                                    entityType={profile}
                                    caption='Zip'
                                    value={() => address?.zip ?? ''}
                                />
                            </AgTechFormRow>
                        </div>
                    </div>
                )
            }}
        </AgTechViewDialog>
    )
}

MyProfileDialog.Open = (e: AgTechClickEventHandler, props: MyProfileDialogProps) => e.dialogs.openDialog({
    dialog: () => <MyProfileDialog {...props} />
})

MyProfileDialog.Button = (props: MyProfileDialogProps & AgTechButtonProps) => (
    <AgTechButton
        text='My Profile'
        icon='fas fa-user-edit'
        {...props}
        action={e => MyProfileDialog.Open(e, props)}
    />
)

export default MyProfileDialog
